import React, { FunctionComponent, useEffect } from 'react';
import {
  ArrowBubbleError,
  Label,
  QueryLoaderWrapper,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@companion-professional/components';
import { cn } from '@companion-professional/webutils';
import { useClinicUsers } from '../../hooks/useClinicUsers';
import { useUserId } from '../../state/auth';

interface AnsweredBySelectorProps {
  // answeredByUserId is the user id of the user who is answering (or has answered) the questions on this evaluation.
  answeredByUserId: string;

  // onAnsweredByUserIdChange is a function that is called when the user selects a user from the dropdown.  The
  // component calling this function in responsible for updating the state and passing the new value (answeredByUserId)
  // back to this component.
  onAnsweredByUserIdChange: (userId: string) => void;

  // originalAnsweredByUserId is the user id of the user who originally answered the questions on this evaluation.
  // This is used to display the original user in the dropdown if the user is an admin user (i.e. filtered out form the
  // list of clinic users).
  originalAnsweredByUserId?: string;

  // validationError is an optional error message to display to the user if the answered by user is invalid.
  validationError?: string;
}

export const AnsweredBySelector: FunctionComponent<AnsweredBySelectorProps> = ({
  answeredByUserId,
  onAnsweredByUserIdChange,
  validationError,
  originalAnsweredByUserId
}) => {
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const currentUserId = useUserId();
  const { users, isPending, error } = useClinicUsers();

  useEffect(() => {
    if (validationError) {
      // If the validation error goes from undefined to a value, scroll to this section so it is visible to the user
      sectionRef.current?.scrollIntoView();
    }
  }, [validationError]);

  return (
    <div className="my-2 flex flex-col space-y-2" ref={sectionRef}>
      <Label description="The user who is answering the questions on this evaluation.">Answered By</Label>
      <QueryLoaderWrapper error={error} isPending={isPending} size="small">
        <Select
          value={answeredByUserId || ''}
          onValueChange={(value) => {
            onAnsweredByUserIdChange(value);
          }}
        >
          <SelectTrigger className="w-[300px]">
            <SelectValue placeholder="Select a user" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {(users || [])
                .sort((a, b) => {
                  if (a.last_name === b.last_name) {
                    return a.first_name.localeCompare(b.first_name);
                  }
                  return a.last_name.localeCompare(b.last_name);
                })
                .map((user) => (
                  <SelectItem
                    key={`'select-answered-by-${user.user_id}`}
                    value={user.user_id}
                    className={cn('', {
                      'font-semibold': user.user_id === currentUserId
                    })}
                  >
                    {`${user.first_name} ${user.last_name}`.trim()} {user.user_id === currentUserId ? '(You)' : ''}
                  </SelectItem>
                ))}

              {/* If the original answered by user is not in the list of returned clinic users (this could happen if a
              global Shepherd Lens admin is marked as the reviewedBy), then we display it as an option.  I feel
              like this is better than hiding the information.  Also, because we don't know have the user in the array
              of Users, we give it a generic name: Shepherd Lens Admin */}
              {typeof originalAnsweredByUserId === 'string' &&
              !(users || []).find((u) => u.user_id === originalAnsweredByUserId) ? (
                <SelectItem
                  key={`'select-answered-by-${originalAnsweredByUserId}`}
                  value={originalAnsweredByUserId}
                  className="italic"
                >
                  Shepherd Lens Admin
                </SelectItem>
              ) : null}
            </SelectGroup>
          </SelectContent>
        </Select>
      </QueryLoaderWrapper>
      <ArrowBubbleError message={validationError} />
    </div>
  );
};
